import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { generateCostKey, calculateUpgradeCost, calculateUpgradeSteps, validateUpgradeParams, formatNumber } from '../utils/dataUtils';
import { useLanguage } from '../contexts/LanguageContext';
import { useSound } from '../contexts/SoundContext';
import StarRating from './StarRating';
import LevelRating from './LevelRating';

const rarityColorMap = {
  common: '#D1D5DB', // light gray
  uncommon: '#6EE7B7', // light green
  rare: '#93C5FD', // light blue
  epic: '#C084FC', // light purple
  legendary: '#FDE047' // light yellow
};

const getRarityColor = (rarity) => {
  if (!rarity) return '#6b7280';
  // Handle epic variants (epic_a, epic_b, epic_c)
  const rarityLower = rarity.toLowerCase();
  if (rarityLower.startsWith('epic')) {
    return rarityColorMap.epic;
  }
  return rarityColorMap[rarityLower] || '#6b7280';
};

const UpgradeCalculator = ({ items, costs }) => {
  const { t } = useLanguage();
  const { playButtonClick, playButtonHover, playSelectOptionOpen, playInterfaceSelectionHover } = useSound();
  const assetBase = typeof window !== 'undefined' ? window.location.origin : '';
  const itemImageFallback = useMemo(() => `${assetBase}/mech_weapon_images/default.png`, [assetBase]);

  // Memoize image path function
  const getItemImage = useCallback((item) => {
    if (!item) {
      return itemImageFallback;
    }
    if (item.image_url) {
      if (item.image_url.startsWith('http')) {
        return item.image_url;
      }
      const normalized = item.image_url.startsWith('/') ? item.image_url : `/${item.image_url}`;
      return `${assetBase}${normalized}`;
    }
    // Fallback to slugified item name
    const slug = (item.list || '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
    return `${assetBase}/mech_weapon_images/${slug}.png`;
  }, [assetBase, itemImageFallback]);
  
  // State for form inputs
  const [selectedItem, setSelectedItem] = useState(null);
  const [itemQuery, setItemQuery] = useState("");
  const [rarityFilter, setRarityFilter] = useState("all"); // all | common | uncommon | rare | epic | legendary
  const [isItemListOpen, setIsItemListOpen] = useState(false);
  const [currentStar, setCurrentStar] = useState(3);
  const [currentLevel, setCurrentLevel] = useState(1);
  const [upgradeToMax, setUpgradeToMax] = useState(true);
  const [targetStar, setTargetStar] = useState(6);
  const [targetLevel, setTargetLevel] = useState(7);
  
  // State for results and errors
  const [results, setResults] = useState(null);
  const [error, setError] = useState(null);
  const [isCalculating, setIsCalculating] = useState(false);
  const [upgradeSteps, setUpgradeSteps] = useState([]);
  const [showBreakdown, setShowBreakdown] = useState(false);
  const [hasSeenBreakdownHint, setHasSeenBreakdownHint] = useState(false);
  const [animateResults, setAnimateResults] = useState(false);
  
  // State for My Resources
  const [useMyResources, setUseMyResources] = useState(false);
  const [myAcoins, setMyAcoins] = useState(() => {
    // Load A-coins from localStorage on initial render
    const savedAcoins = localStorage.getItem('mecharena-my-acoins');
    return savedAcoins || "";
  });
  const [myBlueprints, setMyBlueprints] = useState("");
  
  // State for weekly earning details modal
  const [showWeeklyEarningModal, setShowWeeklyEarningModal] = useState(false);
  
  // Ref for scrolling to results
  const resultsRef = useRef(null);
  
  // Save A-coins to localStorage whenever it changes
  useEffect(() => {
    if (myAcoins !== "") {
      localStorage.setItem('mecharena-my-acoins', myAcoins);
    } else {
      // Remove from localStorage if empty
      localStorage.removeItem('mecharena-my-acoins');
    }
  }, [myAcoins]);

  const dataStartsFromStar = {
    common: 1,
    uncommon: 2,
    rare: 3,
    epic_a: 4,
    epic_b: 3,
    epic_c: 4,
    legendary: 1,
  };

  const aCoinCostPerBluePrint = {
    common: 2,
    uncommon: 4,
    rare: 12,
    epic_a: 48,
    epic_b: 48,
    epic_c: 48,
    legendary: 240,
  };

  // Handle item selection - memoized
  const handleItemSelect = useCallback((itemName) => {
    playInterfaceSelectionHover();
    const item = items.find(i => i.list === itemName);
    setSelectedItem(item);
    setItemQuery(item ? item.list : "");
    setIsItemListOpen(false);
    setError(null);
    setResults(null);
    
    // Set minimum star level based on rarity
    if (item) {
      const minStar = dataStartsFromStar[item.rarity] || 1;
      setCurrentStar(prev => prev < minStar ? minStar : prev);
    }
    
    // Track item selection in Google Analytics
    if (typeof gtag !== 'undefined') {
      gtag('event', 'item_selected', {
        'item_name': itemName,
        'item_type': item?.type,
        'item_rarity': item?.rarity
      });
    }
  }, [items, playInterfaceSelectionHover]);

  // Memoize filtered items - only recalculate when items, rarityFilter, or itemQuery changes
  const filteredItems = useMemo(() => {
    return items
      .filter((i) => {
        if (rarityFilter === 'all') return true;
        const r = (i.rarity || '').toLowerCase();
        if (rarityFilter === 'epic') return r.startsWith('epic');
        return r === rarityFilter;
      })
      .filter((i) => {
        const q = (itemQuery || '').toLowerCase();
        if (!q) return true;
        return (
          i.list.toLowerCase().includes(q) ||
          (i.type || '').toLowerCase().includes(q) ||
          (i.rarity || '').toLowerCase().includes(q)
        );
      });
  }, [items, rarityFilter, itemQuery]);

  // Memoize selected item rarity color
  const selectedItemRarityColor = useMemo(() => {
    return selectedItem ? getRarityColor(selectedItem.rarity) : null;
  }, [selectedItem]);

  // Close dropdown on Escape
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') setIsItemListOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // One-time hint animation timer: auto-mark seen after a short period (per session)
  useEffect(() => {
    if (results && !hasSeenBreakdownHint) {
      // Start animation after results appear
      const timer = setTimeout(() => {
        setHasSeenBreakdownHint(true);
      }, 8000); // 8s gentle pulse then stop
      return () => clearTimeout(timer);
    }
  }, [results, hasSeenBreakdownHint]);

  useEffect(() => {
    if (results) {
      setAnimateResults(true);
      const timer = setTimeout(() => setAnimateResults(false), 700);
      return () => clearTimeout(timer);
    }
  }, [results]);

  // Handle form submission - memoized
  const handleCalculate = useCallback(() => {
    setError(null);
    setResults(null);
    setIsCalculating(true);
    setShowBreakdown(false);
    setHasSeenBreakdownHint(false); // Reset hint animation for new calculation

    if (!selectedItem) {
      setError('Please select a mech or weapon');
      setIsCalculating(false);
      return;
    }

    // Determine target values
    const finalTargetStar = upgradeToMax ? 6 : targetStar;
    const finalTargetLevel = upgradeToMax ? 7 : targetLevel;

    // Validate parameters
    const validation = validateUpgradeParams(currentStar, currentLevel, finalTargetStar, finalTargetLevel);
    if (!validation.isValid) {
      setError(t(validation.error));
      setIsCalculating(false);
      // Track validation error
      if (typeof gtag !== 'undefined') {
        gtag('event', 'error_occurred', {
          'error_type': 'validation',
          'error_message': validation.error,
          'calculator_type': 'mech_weapon'
        });
      }
      return;
    }

    try {
      // Generate cost key
      const costKey = generateCostKey(selectedItem);
      console.log(costKey);
      // Calculate upgrade cost
      const totalCosts = calculateUpgradeCost(
        costs,
        costKey,
        currentStar,
        currentLevel,
        finalTargetStar,
        finalTargetLevel
      );

      // Calculate upgrade steps for detailed breakdown
      const steps = calculateUpgradeSteps(
        costs,
        costKey,
        currentStar,
        currentLevel,
        finalTargetStar,
        finalTargetLevel
      );

      // Calculate resource analysis if My Resources is enabled
      let resourceAnalysis = null;
      if (useMyResources && (myAcoins !== "" || myBlueprints !== "")) {
        const userAcoins = parseFloat(myAcoins) || 0;
        const userBlueprints = parseFloat(myBlueprints) || 0;
        const neededBlueprints = totalCosts.blueprints;
        const remainingBlueprints = Math.max(0, neededBlueprints - userBlueprints);
        
        // Get A-coin cost per blueprint based on item rarity
        const costPerBlueprint = aCoinCostPerBluePrint[selectedItem.rarity] || 0;
        const acoinsNeededForBlueprints = remainingBlueprints * costPerBlueprint;
        
        // Calculate total A-coins needed (upgrade cost + blueprints cost)
        const totalAcoinsNeeded = totalCosts.acoins + acoinsNeededForBlueprints;
        const remainingAcoins = Math.max(0, totalAcoinsNeeded - userAcoins);
        const acoinsSufficient = remainingAcoins === 0;
        
        resourceAnalysis = {
          userAcoins,
          userBlueprints,
          neededBlueprints,
          remainingBlueprints,
          acoinsNeededForBlueprints,
          blueprintsSufficient: remainingBlueprints === 0,
          costPerBlueprint,
          totalAcoinsNeeded,
          remainingAcoins,
          acoinsSufficient,
          upgradeAcoins: totalCosts.acoins
        };
        
        // Track resource analysis result
        if (typeof gtag !== 'undefined') {
          gtag('event', 'resource_analysis', {
            'acoins_sufficient': acoinsSufficient,
            'blueprints_sufficient': remainingBlueprints === 0,
            'total_acoins_needed': totalAcoinsNeeded,
            'calculator_type': 'mech_weapon'
          });
        }
      }

      setResults({
        item: selectedItem,
        from: `${currentStar}★ L${currentLevel}`,
        to: `${finalTargetStar}★ L${finalTargetLevel}`,
        costs: totalCosts,
        resourceAnalysis
      });
      
      setUpgradeSteps(steps);
       
       // Track calculation completion in Google Analytics
       if (typeof gtag !== 'undefined') {
         gtag('event', 'calculation_completed', {
           'item_name': selectedItem.list,
           'item_type': selectedItem.type,
           'item_rarity': selectedItem.rarity,
           'from_star': currentStar,
           'from_level': currentLevel,
           'to_star': finalTargetStar,
           'to_level': finalTargetLevel,
           'total_credits': totalCosts.credits,
           'total_acoins': totalCosts.acoins,
           'total_blueprints': totalCosts.blueprints
         });
       }
       
       // Scroll to results after a short delay to ensure DOM update
       setTimeout(() => {
         resultsRef.current?.scrollIntoView({ 
           behavior: 'smooth', 
           block: 'start' 
         });
       }, 100);
    } catch (err) {
      setError(`Calculation error: ${err.message}`);
      // Track calculation error
      if (typeof gtag !== 'undefined') {
        gtag('event', 'error_occurred', {
          'error_type': 'calculation',
          'error_message': err.message,
          'calculator_type': 'mech_weapon'
        });
      }
    } finally {
      setIsCalculating(false);
    }
  }, [selectedItem, upgradeToMax, targetStar, targetLevel, currentStar, currentLevel, costs, useMyResources, myAcoins, myBlueprints, t, playButtonClick]);

  const getResultCardAnimationStyle = (delay = 0) => {
    if (!animateResults) return {};
    return { animationDelay: `${delay}ms` };
  };

  return (
    <div className="bg-gradient-to-br from-white via-blue-50 to-indigo-50 dark:from-[#121212] dark:via-[#1E1E1E] dark:to-[#1A1A1A] rounded-2xl shadow-xl p-6 sm:p-8 lg:p-10 border border-blue-100 dark:border-[#2A2A2A] max-w-4xl mx-auto transition-colors duration-300">
      <div className="hero-animated-bg mb-6 sm:mb-8 lg:mb-10 text-center rounded-2xl p-5 sm:p-6 lg:p-7 dark:p-8 border border-blue-200/60 dark:border-[#FF8A65]/40 shadow-inner">
        <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold bg-gradient-to-r from-blue-50/90 to-white bg-clip-text text-transparent dark:bg-none dark:text-white dark:font-semibold dark:text-[32px]">
          <span className="dark:hidden text-white [text-shadow:2px_2px_4px_#000000]">{t('app.calculatorTitle')}</span>
          <span className="hidden dark:inline">{t('app.calculatorTitle')}</span>
        </h2>
      </div>
      
      {/* Rarity Filter + Item Selection - Searchable Combobox */}
      <div className="mb-6 sm:mb-8">
        {/* Rarity Filter */}
        <div className="mb-3">
          <label className="block text-xs sm:text-sm font-medium text-indigo-700 dark:text-white mb-1">
            {t('form.rarityLabel')}
          </label>
          <select
            value={rarityFilter}
            onChange={(e) => { 
              setRarityFilter(e.target.value); 
              setIsItemListOpen(true);
              // Track filter change
              if (typeof gtag !== 'undefined') {
                gtag('event', 'filter_changed', {
                  'filter_type': 'rarity',
                  'filter_value': e.target.value,
                  'calculator_type': 'mech_weapon'
                });
              }
            } }
            className="w-full px-3 py-2 rounded-lg border-2 border-indigo-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm text-gray-800 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:focus:ring-orange-glow focus:border-indigo-500 dark:focus:border-[#FF8A65] transition-colors"
            aria-label={t('form.rarityLabel')}
          >
            <option value="all">{t('form.rarityAll')}</option>
            <option value="common">{t('form.rarityCommon')}</option>
            <option value="uncommon">{t('form.rarityUncommon')}</option>
            <option value="rare">{t('form.rarityRare')}</option>
            <option value="epic">{t('form.rarityEpic')}</option>
            <option value="legendary">{t('form.rarityLegendary')}</option>
          </select>
        </div>

        <label className="block text-sm sm:text-base font-medium text-indigo-700 dark:text-white mb-2 sm:mb-3">
          {t('form.selectItem')}
        </label>
        <div className="relative">
          <div className="flex items-center rounded-xl border-2 border-indigo-200 dark:border-gray-600 bg-white/80 dark:bg-gray-700/80 backdrop-blur-sm focus-within:ring-2 focus-within:ring-indigo-500 focus-within:border-indigo-500 transition-all duration-200 hover:border-indigo-300 dark:hover:border-gray-500 hover:shadow-md">
            <svg className="w-5 h-5 text-indigo-400 dark:text-[#FFB38A] ml-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-4.35-4.35m0 0A7.5 7.5 0 104.5 4.5a7.5 7.5 0 0012.15 12.15z" />
            </svg>
            <input
              type="text"
              value={itemQuery}
              placeholder={selectedItem ? selectedItem.list : t('form.searchPlaceholder')}
              onFocus={() => {
                if (!isItemListOpen) {
                  playSelectOptionOpen();
                }
                setIsItemListOpen(true);
              }}
              onChange={(e) => { 
                if (!isItemListOpen) {
                  playSelectOptionOpen();
                }
                setItemQuery(e.target.value); 
                setIsItemListOpen(true);
                // Track search query (only if query length > 2 to avoid noise)
                if (typeof gtag !== 'undefined' && e.target.value.length > 2) {
                  gtag('event', 'search_performed', {
                    'search_term_length': e.target.value.length,
                    'calculator_type': 'mech_weapon'
                  });
                }
              } }
            className="w-full px-3 sm:px-4 py-2 sm:py-3 rounded-xl outline-none text-sm sm:text-base bg-transparent text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400"
              aria-label="Search items"
            />
            {selectedItem && (
              <button
                type="button"
                className="mr-3 text-indigo-500 dark:text-[#FF7043] text-xs underline hover:text-indigo-700 dark:hover:text-[#FF8A65]"
                onClick={() => { 
                  playButtonClick();
                  setSelectedItem(null); 
                  setItemQuery(""); 
                  setIsItemListOpen(true); 
                }}
              >
                {t('form.clear')}
              </button>
            )}
          </div>

          {isItemListOpen && (
            <div className="absolute z-20 mt-2 w-full max-h-64 overflow-auto bg-white dark:bg-gray-800 border-2 border-indigo-200 dark:border-gray-600 rounded-xl shadow-lg">
              {filteredItems.length === 0 ? (
                <div className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">No results</div>
              ) : (
                filteredItems.map((item) => (
                  <button
                    type="button"
                    key={item.list}
                    onClick={() => handleItemSelect(item.list)}
                    className={`w-full text-left px-4 py-2 text-sm sm:text-base hover:bg-indigo-50 dark:hover:bg-[#2F1E13] ${selectedItem?.list === item.list ? 'bg-indigo-50 dark:bg-[#432615]' : ''} text-gray-900 dark:text-gray-100`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-medium text-gray-800 dark:text-gray-200">{item.list}</span>
                      <span className="text-xs text-gray-500 dark:text-gray-400">{item.type} • {item.rarity}{item.energy ? ` • ${item.energy} EN` : ''}</span>
                    </div>
                  </button>
                ))
              )}
            </div>
          )}
        </div>
      </div>

      {/* Purchase Cost Display */}
      {selectedItem && selectedItem.cost && (
        <div className="mt-3 mb-6 p-3 bg-blue-50 dark:bg-white/10 rounded-lg border border-blue-200 dark:border-[#FF8A65] dark:shadow-[0_0_20px_rgba(255,120,49,0.25)] flex items-center gap-4">
          <div className="flex-shrink-0">
            <img
              src={getItemImage(selectedItem)}
              alt={selectedItem.list}
              className="w-24 h-24 sm:w-28 sm:h-28 lg:w-32 lg:h-32 rounded-2xl border-2 border-blue-200 object-contain bg-white dark:bg-[#1A1A1A] p-1"
              style={{ borderColor: selectedItemRarityColor }}
              onError={(e) => {
                if (e.target.src !== itemImageFallback) {
                  e.target.src = itemImageFallback;
                }
              }}
            />
          </div>
          <div className="text-xs sm:text-sm text-blue-700 dark:text-blue-300 flex-1 space-y-1">
            <div className="font-semibold text-sm sm:text-base text-blue-900 dark:text-blue-100">
              {selectedItem.list}
            </div>
            <div className="flex flex-wrap gap-2 text-[11px] sm:text-xs">
              <span className="inline-flex items-center gap-1 bg-white/80 dark:bg-[#1A1A1A]/80 px-2 py-1 rounded-lg border border-blue-200 dark:border-[#FF8A65] text-blue-800 dark:text-blue-200">
                <svg className="w-3.5 h-3.5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                </svg>
                {(t('form.type') === 'form.type' ? 'Type' : t('form.type'))}: {selectedItem.type || 'N/A'}
              </span>
              <span
                className="inline-flex items-center gap-1 bg-white/80 dark:bg-[#1A1A1A]/70 px-2 py-1 rounded-lg border text-slate-800 dark:text-white"
                style={{ borderColor: selectedItemRarityColor }}
              >
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ color: selectedItemRarityColor }}>
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8c-1.657 0-3 1.343-3 3s1.343 3 3 3 3-1.343 3-3-1.343-3-3-3z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 5v1m-7 7h1m12 0h1m-7 7v-1m-5.657-5.657l.707-.707m8.486 0l.707.707m0-8.486l-.707.707m-8.486 0l-.707-.707" />
                </svg>
                {(t('form.rarityLabel') === 'form.rarityLabel' ? 'Rarity' : t('form.rarityLabel'))}: {selectedItem.rarity || 'N/A'}
              </span>
            </div>
            <div className="pt-1 flex items-center flex-wrap gap-1.5">
              <strong>{(t('form.purchaseCost') === 'form.purchaseCost' ? 'Purchase Cost' : t('form.purchaseCost'))}:</strong>
              {selectedItem.cost.currency === 'Paid' ? (
                <span>{t('form.purchaseCostPaid') === 'form.purchaseCostPaid' ? 'Paid' : t('form.purchaseCostPaid')}</span>
              ) : selectedItem.cost.currency === 'Credits' ? (
                <span className="flex items-center gap-1.5">
                  <img src={`${assetBase}/credits.webp`} alt="Credits" className="w-5 h-5 sm:w-6 sm:h-6" />
                  {formatNumber(selectedItem.cost.amount)}
                </span>
              ) : (
                <span className="flex items-center gap-1.5">
                  <img src={`${assetBase}/acoin.webp`} alt="A-Coins" className="w-5 h-5 sm:w-6 sm:h-6" />
                  {formatNumber(selectedItem.cost.amount)}
                </span>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Current Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 sm:gap-8 mb-6 sm:mb-8">
        <div className="flex justify-center sm:justify-start">
          <div className="text-center">
            <StarRating
              value={currentStar}
              onChange={setCurrentStar}
              maxStars={6}
              minStars={selectedItem ? dataStartsFromStar[selectedItem.rarity] || 1 : 1}
              label={t('form.currentStarLevel')}
            />
            {selectedItem && (
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                {t('form.minStars', { min: dataStartsFromStar[selectedItem.rarity] || 1, rarity: selectedItem.rarity })}
              </p>
            )}
          </div>
        </div>
        
        <div className="flex justify-center sm:justify-start">
          <LevelRating
            value={currentLevel}
            onChange={setCurrentLevel}
            maxLevels={7}
            label={t('form.currentLevel')}
          />
        </div>
      </div>

      {/* Upgrade to Max Checkbox */}
      <div className="mb-6 sm:mb-8">
        <label 
          className="flex items-center p-3 sm:p-4 bg-gradient-to-r from-indigo-50 to-blue-50 dark:from-[#1E1E1E] dark:to-[#1A1A1A] rounded-xl border-2 border-indigo-200 dark:border-[#444444] hover:border-indigo-300 dark:hover:border-[#FF8A65] hover:from-indigo-100 hover:to-blue-100 dark:hover:from-[#272727] dark:hover:to-[#2F2F2F] transition-all duration-300 cursor-pointer hover:shadow-md hover:scale-[1.02] active:scale-[0.98]"

        >
          <input
            type="checkbox"
            checked={upgradeToMax}
            onChange={(e) => {
              playButtonClick();
              setUpgradeToMax(e.target.checked);
              // Track feature usage
              if (typeof gtag !== 'undefined') {
                gtag('event', 'feature_used', {
                  'feature_name': 'upgrade_to_max',
                  'enabled': e.target.checked,
                  'calculator_type': 'mech_weapon'
                });
              }
            }}
            className="mr-3 w-5 h-5 text-indigo-600 border-indigo-300 rounded focus:ring-indigo-500 dark:focus:ring-orange-glow focus:ring-2"
          />
          <span className="text-base font-medium text-indigo-800 dark:text-[#FF9800]">
            {t('form.upgradeToMax')}
          </span>
        </label>
      </div>

      {/* Target Stats (only show if not upgrading to max) */}
      {!upgradeToMax && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-6 sm:mb-8">
          <div className="flex justify-center sm:justify-start">
            <div className="text-center">
              <StarRating
                value={targetStar}
                onChange={setTargetStar}
                maxStars={6}
                minStars={selectedItem ? dataStartsFromStar[selectedItem.rarity] || 1 : 1}
                label={t('form.targetStarLevel')}
              />
              {selectedItem && (
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  {t('form.minStars', { min: dataStartsFromStar[selectedItem.rarity] || 1, rarity: selectedItem.rarity })}
                </p>
              )}
            </div>
          </div>
          
          <div className="flex justify-center sm:justify-start">
            <LevelRating
              value={targetLevel}
              onChange={setTargetLevel}
              maxLevels={7}
              label={t('form.targetLevel')}
            />
          </div>
        </div>
      )}

      {/* My Resources Section */}
      <div className="mb-6 sm:mb-8">
        <label 
          className="flex items-center p-3 sm:p-4 bg-gradient-to-r from-emerald-50 to-green-50 dark:from-[#1E1E1E] dark:to-[#1A1A1A] rounded-xl border-2 border-emerald-200 dark:border-[#444444] hover:border-emerald-300 dark:hover:border-[#FF8A65] hover:from-emerald-100 hover:to-green-100 dark:hover:from-[#272727] dark:hover:to-[#2F2F2F] transition-all duration-300 cursor-pointer hover:shadow-md hover:scale-[1.02] active:scale-[0.98]"

        >
          <input
            type="checkbox"
            checked={useMyResources}
            onChange={(e) => {
              playButtonClick();
              setUseMyResources(e.target.checked);
              if (!e.target.checked) {
                // Don't clear A-coins, just blueprints, so user doesn't lose their saved A-coins
                setMyBlueprints("");
              }
              // Track feature usage
              if (typeof gtag !== 'undefined') {
                gtag('event', 'feature_used', {
                  'feature_name': 'my_resources',
                  'enabled': e.target.checked,
                  'calculator_type': 'mech_weapon'
                });
              }
            }}
            className="mr-3 w-5 h-5 text-emerald-600 border-emerald-300 rounded focus:ring-emerald-500 focus:ring-2"
          />
          <span className="text-base font-medium text-emerald-800 dark:text-emerald-200">
            {t('form.myResources')}
          </span>
        </label>
        {useMyResources && (
          <div className="mt-4 p-4 bg-gradient-to-r from-emerald-50 to-green-50 dark:from-gray-700 dark:to-gray-600 rounded-xl border-2 border-emerald-200 dark:border-gray-500">
            <p className="text-sm text-emerald-700 dark:text-emerald-300 mb-4">
              {t('form.myResourcesDescription')}
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-emerald-800 dark:text-emerald-200 mb-2">
                  {t('form.myAcoins')}
                </label>
                <input
                  type="number"
                  min="0"
                  step="1"
                  value={myAcoins}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value === "" || (parseFloat(value) >= 0 && !isNaN(value))) {
                      setMyAcoins(value);
                    }
                  }}
                  placeholder="0"
                  className="w-full px-3 py-2 rounded-lg border-2 border-emerald-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm text-gray-800 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-colors"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-emerald-800 dark:text-emerald-200 mb-2">
                  {t('form.myBlueprints')}
                </label>
                <input
                  type="number"
                  min="0"
                  step="1"
                  value={myBlueprints}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value === "" || (parseFloat(value) >= 0 && !isNaN(value))) {
                      setMyBlueprints(value);
                    }
                  }}
                  placeholder="0"
                  className="w-full px-3 py-2 rounded-lg border-2 border-emerald-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm text-gray-800 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-colors"
                />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Calculate Button */}
      <button
        onClick={() => {
          playButtonClick();
          handleCalculate();
        }}
        onMouseEnter={playButtonHover}
        disabled={isCalculating}
        className={`w-full py-3 sm:py-4 px-4 sm:px-6 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:focus:ring-orange-glow focus:ring-offset-2 text-base sm:text-lg font-semibold shadow-lg ${
          isCalculating 
            ? 'bg-gray-400 cursor-not-allowed' 
            : 'bg-gradient-to-r from-indigo-600 to-blue-600 hover:from-indigo-700 hover:to-blue-700 highlighter-hover btn-orange-gradient'
        }`}
      >
        <span className="flex items-center justify-center text-white">
          {isCalculating ? (
            <>
              <svg className="w-5 h-5 mr-2 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              {t('form.calculating')}
            </>
          ) : (
            <>
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
              </svg>
              {t('form.calculate')}
            </>
          )}
        </span>
      </button>

      {/* Error Display */}
      {error && (
        <div className="mt-4 sm:mt-6 bg-gradient-to-r from-red-50 to-pink-50 border-2 border-red-300 text-red-700 px-4 sm:px-6 py-3 sm:py-4 rounded-xl shadow-md animate-in slide-in-from-top-2 duration-300">
          <div className="flex items-center">
            <svg className="w-4 sm:w-5 h-4 sm:h-5 mr-2 animate-bounce" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
            <span className="text-sm sm:text-base">{error}</span>
          </div>
        </div>
      )}

      {/* Results Display */}
      {results && (
        <div 
          ref={resultsRef}
          className="mt-6 sm:mt-8 bg-gradient-to-br from-green-50 to-emerald-50 border-2 border-green-200 rounded-2xl p-4 sm:p-6 shadow-lg animate-in slide-in-from-bottom-2 duration-500"
        >
          <h3 className="text-lg sm:text-xl font-bold text-green-800 mb-3 sm:mb-4 text-center">{t('results.totalCost')}</h3>
          <div className="space-y-2">
            <p className="text-sm text-green-700">
              <strong>Item:</strong> {results.item.list} ({results.item.type} - {results.item.rarity})
            </p>
            <p className="text-sm text-green-700">
              <strong>{t('results.upgradeFrom', { from: results.from, to: results.to })}</strong>
            </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 mt-4 sm:mt-6">
                <div
                  className={`bg-gradient-to-br from-white to-blue-50 p-4 sm:p-6 rounded-xl border-2 border-blue-200 text-center shadow-md hover:shadow-lg transition-all duration-300 transform hover:scale-105 hover:border-blue-300 hover:shadow-xl group ${animateResults ? 'result-card-pop' : ''}`}
                  style={getResultCardAnimationStyle(0)}
                >
                   <div className="text-xs sm:text-sm font-medium text-blue-700 mb-2 sm:mb-3 group-hover:text-blue-800 transition-colors duration-200">{t('results.credits')}</div>
                   <div className="flex items-center justify-center space-x-2 sm:space-x-3">
                     <img src="./credits.webp" alt="Credits" className="w-6 h-6 sm:w-8 sm:h-8 group-hover:scale-110 transition-transform duration-200" />
                     <span className="text-xl sm:text-2xl lg:text-3xl font-bold text-blue-600 group-hover:text-blue-700 transition-colors duration-200">{formatNumber(results.costs.credits)}</span>
                   </div>
                 </div>
                <div
                  className={`bg-gradient-to-br from-white to-purple-50 p-4 sm:p-6 rounded-xl border-2 border-purple-200 text-center shadow-md hover:shadow-lg transition-all duration-300 transform hover:scale-105 hover:border-purple-300 hover:shadow-xl group ${animateResults ? 'result-card-pop' : ''}`}
                  style={getResultCardAnimationStyle(120)}
                >
                   <div className="text-xs sm:text-sm font-medium text-purple-700 mb-2 sm:mb-3 group-hover:text-purple-800 transition-colors duration-200">{t('results.acoins')}</div>
                   <div className="flex items-center justify-center space-x-2">
                     <img src="./acoin.webp" alt="A-Coins" className="w-6 h-6 sm:w-8 sm:h-8 group-hover:scale-110 transition-transform duration-200" />
                    <span className="text-xl sm:text-2xl lg:text-3xl font-bold" style={{ color: "#c004ad"}}>{formatNumber(results.costs.acoins)}</span>
                   </div>
                 </div>
                <div
                  className={`bg-gradient-to-br from-white to-emerald-50 p-4 sm:p-6 rounded-xl border-2 border-emerald-200 text-center shadow-md hover:shadow-lg transition-all duration-300 transform hover:scale-105 hover:border-emerald-300 hover:shadow-xl group sm:col-span-2 lg:col-span-1 ${animateResults ? 'result-card-pop' : ''}`}
                  style={getResultCardAnimationStyle(240)}
                >
                   <div className="text-xs sm:text-sm font-medium text-emerald-700 mb-2 sm:mb-3 group-hover:text-emerald-800 transition-colors duration-200">{t('results.blueprints')}</div>
                   <div className="flex items-center justify-center space-x-2">
                     <img src="./blueprints.png" alt="Blueprints" className="w-6 h-6 sm:w-8 sm:h-8 group-hover:scale-110 transition-transform duration-200" />
                     <span className="text-xl sm:text-2xl lg:text-3xl font-bold text-emerald-600 group-hover:text-emerald-800 transition-colors duration-200">{formatNumber(results.costs.blueprints)}</span>
                   </div>
                 </div>
              </div>
                     </div>

                     {/* Resource Analysis Section */}
                     {results.resourceAnalysis && (
                       <div className="mt-6 sm:mt-8 bg-gradient-to-br from-amber-50 to-orange-50 border-2 border-amber-200 rounded-2xl p-4 sm:p-6 shadow-lg">
                         <h3 className="text-lg sm:text-xl font-bold text-amber-800 mb-4 text-center">{t('results.resourceAnalysis')}</h3>
                         
                         <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                           {/* Available Resources */}
                           <div className="bg-white rounded-xl p-4 border-2 border-amber-200">
                             <h4 className="text-sm font-semibold text-amber-700 mb-3">{t('results.availableResources')}</h4>
                             <div className="space-y-2">
                               <div className="flex items-center justify-between">
                                 <span className="text-sm text-gray-600">{t('results.acoins')}:</span>
                                 <span className="text-sm font-bold" style={{ color: "#c004ad" }}>{formatNumber(results.resourceAnalysis.userAcoins)}</span>
                               </div>
                               <div className="flex items-center justify-between">
                                 <span className="text-sm text-gray-600">{t('results.blueprints')}:</span>
                                 <span className="text-sm font-bold text-emerald-600">{formatNumber(results.resourceAnalysis.userBlueprints)}</span>
                               </div>
                             </div>
                           </div>
                           
                           {/* Needed Resources */}
                           <div className="bg-white rounded-xl p-4 border-2 border-amber-200">
                             <h4 className="text-sm font-semibold text-amber-700 mb-3">{t('results.neededResources')}</h4>
                             <div className="space-y-2">
                               <div className="flex items-center justify-between">
                                 <span className="text-sm text-gray-600">{t('results.acoins')}:</span>
                                 <span className="text-sm font-bold" style={{ color: "#c004ad" }}>{formatNumber(results.resourceAnalysis.totalAcoinsNeeded)}</span>
                               </div>
                               <div className="flex items-center justify-between">
                                 <span className="text-sm text-gray-600">{t('results.blueprints')}:</span>
                                 <span className="text-sm font-bold text-emerald-600">{formatNumber(results.resourceAnalysis.neededBlueprints)}</span>
                               </div>
                             </div>
                           </div>
                         </div>
                         
                         {/* A-Coins Status */}
                         <div className={`mt-4 p-4 rounded-xl border-2 ${
                           results.resourceAnalysis.acoinsSufficient 
                             ? 'bg-green-50 border-green-200' 
                             : 'bg-red-50 border-red-200'
                         }`}>
                           <div className="flex items-center justify-between mb-2">
                             <span className={`text-sm font-semibold ${
                               results.resourceAnalysis.acoinsSufficient 
                                 ? 'text-green-700' 
                                 : 'text-red-700'
                             }`}>
                               {results.resourceAnalysis.acoinsSufficient 
                                 ? t('results.acoinsSufficient')
                                 : t('results.acoinsInsufficient')}
                             </span>
                           </div>
                           
                           {!results.resourceAnalysis.acoinsSufficient && (
                             <div className="mt-3 space-y-2">
                               <div className="flex items-center justify-between text-sm">
                                 <span className="text-gray-600">{t('results.youHave')}:</span>
                                 <span className="font-bold" style={{ color: "#c004ad" }}>{formatNumber(results.resourceAnalysis.userAcoins)}</span>
                               </div>
                               <div className="flex items-center justify-between text-sm">
                                 <span className="text-gray-600">{t('results.totalAcoinsNeeded')}:</span>
                                 <span className="font-bold" style={{ color: "#c004ad" }}>{formatNumber(results.resourceAnalysis.totalAcoinsNeeded)}</span>
                               </div>
                               <div className="text-xs text-gray-500 pl-2">
                                 ({t('results.upgradeAcoins')}: {formatNumber(results.resourceAnalysis.upgradeAcoins)} + {t('results.acoinsNeededForBlueprints')}: {formatNumber(results.resourceAnalysis.acoinsNeededForBlueprints)})
                               </div>
                               <div className="flex items-center justify-between text-sm pt-2 border-t border-red-200">
                                 <span className="text-gray-700 font-medium">{t('results.acoinsNeededExtra')}:</span>
                                 <span className="font-bold text-red-600">{formatNumber(results.resourceAnalysis.remainingAcoins)}</span>
                               </div>
                             </div>
                           )}
                         </div>
                         
                         {/* Blueprint Status */}
                         <div className={`mt-4 p-4 rounded-xl border-2 ${
                           results.resourceAnalysis.blueprintsSufficient 
                             ? 'bg-green-50 border-green-200' 
                             : 'bg-red-50 border-red-200'
                         }`}>
                           <div className="flex items-center justify-between mb-2">
                             <span className={`text-sm font-semibold ${
                               results.resourceAnalysis.blueprintsSufficient 
                                 ? 'text-green-700' 
                                 : 'text-red-700'
                             }`}>
                               {results.resourceAnalysis.blueprintsSufficient 
                                 ? t('results.blueprintsSufficient')
                                 : t('results.blueprintsInsufficient')}
                             </span>
                           </div>
                           
                           {!results.resourceAnalysis.blueprintsSufficient && (
                             <div className="mt-3 space-y-2">
                               <div className="flex items-center justify-between text-sm">
                                 <span className="text-gray-600">{t('results.youHave')}:</span>
                                 <span className="font-bold text-emerald-600">{formatNumber(results.resourceAnalysis.userBlueprints)}</span>
                               </div>
                               <div className="flex items-center justify-between text-sm">
                                 <span className="text-gray-600">{t('results.youNeed')}:</span>
                                 <span className="font-bold text-emerald-600">{formatNumber(results.resourceAnalysis.neededBlueprints)}</span>
                               </div>
                               <div className="flex items-center justify-between text-sm pt-2 border-t border-red-200">
                                 <span className="text-gray-700 font-medium">{t('results.remaining')}:</span>
                                 <span className="font-bold text-red-600">{formatNumber(results.resourceAnalysis.remainingBlueprints)}</span>
                               </div>
                               <div className="mt-3 p-3 bg-white rounded-lg border border-amber-200">
                                 <div className="text-xs text-gray-600 mb-1">{t('results.acoinsNeededForBlueprints')}</div>
                                 <div className="text-lg font-bold text-center" style={{ color: "#c004ad" }}>
                                   {formatNumber(results.resourceAnalysis.acoinsNeededForBlueprints)}
                                 </div>
                                 <div className="text-xs text-gray-500 text-center mt-1">
                                   ({formatNumber(results.resourceAnalysis.costPerBlueprint)} {t('results.acoins')} × {formatNumber(results.resourceAnalysis.remainingBlueprints)} {t('results.blueprints')})
                                 </div>
                               </div>
                             </div>
                           )}
                         </div>
                         
                         {/* Get Weekly Earning Details Button - Show if any resource is insufficient */}
                         {(!results.resourceAnalysis.acoinsSufficient || !results.resourceAnalysis.blueprintsSufficient) && (
                           <div className="mt-4 text-center">
                            <button
                              onClick={() => {
                                setShowWeeklyEarningModal(true);
                                // Track modal open
                                if (typeof gtag !== 'undefined') {
                                  gtag('event', 'modal_opened', {
                                    'modal_name': 'weekly_earning_details',
                                    'calculator_type': 'mech_weapon'
                                  });
                                }
                              }}
                              className="inline-flex items-center space-x-2 px-4 py-2 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-medium rounded-lg shadow-md hover:shadow-lg transition-all duration-200 transform hover:scale-105 btn-orange-gradient"
                            >
                               <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                 <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                               </svg>
                               <span>{t('results.getWeeklyEarningDetails')}</span>
                             </button>
                           </div>
                         )}
                       </div>
                     )}
                     
                     {/* Weekly Earning Details Modal */}
                     {showWeeklyEarningModal && (
                       <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50" onClick={() => setShowWeeklyEarningModal(false)}>
                         <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                           <div className="sticky top-0 bg-white dark:bg-[#1A1A1A] border-b border-gray-200 dark:border-[#2A2A2A] px-6 py-4 flex items-center justify-between rounded-t-2xl">
                             <h3 className="text-xl font-bold text-gray-800 dark:text-gray-200">{t('results.weeklyEarningDetails')}</h3>
                             <button
                               onClick={() => setShowWeeklyEarningModal(false)}
                              className="text-gray-500 hover:text-gray-700 dark:text-[#8A8A8A] dark:hover:text-[#FFFFFF] transition-colors"
                               aria-label={t('results.close')}
                             >
                               <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                 <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                               </svg>
                             </button>
                           </div>
                           <div className="p-6">
                             <h4 className="text-lg font-semibold text-gray-800 dark:text-gray-200 mb-4">{t('results.weeklyEarningTitle')}</h4>
                             <div className="overflow-x-auto">
                               <table className="w-full border-collapse border border-gray-300 dark:border-gray-600">
                                 <thead>
                                   <tr className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-gray-700 dark:to-gray-600">
                                     <th className="border border-gray-300 dark:border-gray-600 px-4 py-3 text-left font-semibold text-gray-800 dark:text-gray-200">{t('results.source')}</th>
                                     <th className="border border-gray-300 dark:border-gray-600 px-4 py-3 text-right font-semibold text-gray-800 dark:text-gray-200">{t('results.weeklyCredits')}</th>
                                     <th className="border border-gray-300 dark:border-gray-600 px-4 py-3 text-right font-semibold text-gray-800 dark:text-gray-200">{t('results.weeklyAcoins')}</th>
                                   </tr>
                                 </thead>
                                 <tbody>
                                   <tr className="hover:bg-gray-50 dark:hover:bg-gray-700">
                                     <td className="border border-gray-300 dark:border-gray-600 px-4 py-3 text-gray-700 dark:text-gray-300">{t('results.dailyObjectives')}</td>
                                     <td className="border border-gray-300 dark:border-gray-600 px-4 py-3 text-right font-medium text-blue-600 dark:text-blue-400">58,450</td>
                                     <td className="border border-gray-300 dark:border-gray-600 px-4 py-3 text-right font-medium" style={{ color: "#c004ad" }}>91</td>
                                   </tr>
                                   <tr className="hover:bg-gray-50 dark:hover:bg-gray-700">
                                     <td className="border border-gray-300 dark:border-gray-600 px-4 py-3 text-gray-700 dark:text-gray-300">{t('results.weeklyObjectives')}</td>
                                     <td className="border border-gray-300 dark:border-gray-600 px-4 py-3 text-right font-medium text-blue-600 dark:text-blue-400">19,535</td>
                                     <td className="border border-gray-300 dark:border-gray-600 px-4 py-3 text-right font-medium" style={{ color: "#c004ad" }}>327</td>
                                   </tr>
                                   <tr className="hover:bg-gray-50 dark:hover:bg-gray-700">
                                     <td className="border border-gray-300 dark:border-gray-600 px-4 py-3 text-gray-700 dark:text-gray-300">{t('results.steelCrates')}</td>
                                     <td className="border border-gray-300 dark:border-gray-600 px-4 py-3 text-right font-medium text-blue-600 dark:text-blue-400">9,450</td>
                                     <td className="border border-gray-300 dark:border-gray-600 px-4 py-3 text-right font-medium" style={{ color: "#c004ad" }}>2</td>
                                   </tr>
                                   <tr className="hover:bg-gray-50 dark:hover:bg-gray-700">
                                     <td className="border border-gray-300 dark:border-gray-600 px-4 py-3 text-gray-700 dark:text-gray-300">{t('results.silverCrates')}</td>
                                     <td className="border border-gray-300 dark:border-gray-600 px-4 py-3 text-right font-medium text-blue-600 dark:text-blue-400">37,800</td>
                                     <td className="border border-gray-300 dark:border-gray-600 px-4 py-3 text-right font-medium" style={{ color: "#c004ad" }}>25</td>
                                   </tr>
                                   <tr className="hover:bg-gray-50 dark:hover:bg-gray-700">
                                     <td className="border border-gray-300 dark:border-gray-600 px-4 py-3 text-gray-700 dark:text-gray-300">{t('results.goldCrates')}</td>
                                     <td className="border border-gray-300 dark:border-gray-600 px-4 py-3 text-right font-medium text-blue-600 dark:text-blue-400">16,200</td>
                                     <td className="border border-gray-300 dark:border-gray-600 px-4 py-3 text-right font-medium" style={{ color: "#c004ad" }}>35</td>
                                   </tr>
                                   <tr className="hover:bg-gray-50 dark:hover:bg-gray-700">
                                     <td className="border border-gray-300 dark:border-gray-600 px-4 py-3 text-gray-700 dark:text-gray-300">{t('results.events')}</td>
                                     <td className="border border-gray-300 dark:border-gray-600 px-4 py-3 text-right font-medium text-blue-600 dark:text-blue-400">63,100</td>
                                     <td className="border border-gray-300 dark:border-gray-600 px-4 py-3 text-right font-medium" style={{ color: "#c004ad" }}>100</td>
                                   </tr>                                   
                                   <tr className="hover:bg-gray-50 dark:hover:bg-gray-700">
                                     <td className="border border-gray-300 dark:border-gray-600 px-4 py-3 text-gray-700 dark:text-gray-300">{t('results.dailyLoginProgram')}</td>
                                     <td className="border border-gray-300 dark:border-gray-600 px-4 py-3 text-right font-medium text-blue-600 dark:text-blue-400">11,775</td>
                                     <td className="border border-gray-300 dark:border-gray-600 px-4 py-3 text-right font-medium" style={{ color: "#c004ad" }}>2</td>
                                   </tr>
                                   <tr className="hover:bg-gray-50 dark:hover:bg-gray-700">
                                     <td className="border border-gray-300 dark:border-gray-600 px-4 py-3 text-gray-700 dark:text-gray-300">{t('results.silverBattlePass')}</td>
                                     <td className="border border-gray-300 dark:border-gray-600 px-4 py-3 text-right font-medium text-blue-600 dark:text-blue-400">10,400</td>
                                     <td className="border border-gray-300 dark:border-gray-600 px-4 py-3 text-right font-medium" style={{ color: "#c004ad" }}>8</td>
                                   </tr>
                                   <tr className="hover:bg-gray-50 dark:hover:bg-gray-700">
                                     <td className="border border-gray-300 dark:border-gray-600 px-4 py-3 text-gray-700 dark:text-gray-300">{t('results.adsMobileOnly')}</td>
                                     <td className="border border-gray-300 dark:border-gray-600 px-4 py-3 text-right font-medium text-blue-600 dark:text-blue-400">202,300</td>
                                     <td className="border border-gray-300 dark:border-gray-600 px-4 py-3 text-right font-medium" style={{ color: "#c004ad" }}>71</td>
                                   </tr>
                                   <tr className="bg-gradient-to-r from-green-50 to-emerald-50 dark:from-gray-700 dark:to-gray-600 font-bold">
                                     <td className="border border-gray-300 dark:border-gray-600 px-4 py-3 text-gray-800 dark:text-gray-200">{t('results.total')}</td>
                                     <td className="border border-gray-300 dark:border-gray-600 px-4 py-3 text-right text-blue-700 dark:text-blue-400">429,010</td>
                                     <td className="border border-gray-300 dark:border-gray-600 px-4 py-3 text-right" style={{ color: "#c004ad" }}>661</td>
                                   </tr>
                                 </tbody>
                               </table>
                             </div>
                             <div className="mt-4 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
                               <p className="text-sm text-amber-800 dark:text-amber-200 text-center">
                                 <span className="font-medium">{t('results.weeklyEarningNote')}</span>
                                 <span className="mx-2">•</span>
                                 <span>{t('results.weeklyEarningUpdated', { date: '8-Nov-2025' })}</span>
                               </p>
                             </div>
                           </div>
                         </div>
                       </div>
                     )}
                     
                     {/* Detailed Breakdown Table */}
                     <div className="mt-6 sm:mt-8">
                      <button 
                        onClick={() => {
                          if (!hasSeenBreakdownHint) setHasSeenBreakdownHint(true);
                          const newShowBreakdown = !showBreakdown;
                          setShowBreakdown(newShowBreakdown);
                          // Track breakdown view
                          if (typeof gtag !== 'undefined') {
                            gtag('event', 'feature_used', {
                              'feature_name': 'detailed_breakdown',
                              'action': newShowBreakdown ? 'opened' : 'closed',
                              'calculator_type': 'mech_weapon'
                            });
                          }
                        }}
                        className={`flex items-center space-x-2 text-sm text-green-600 hover:text-green-700 underline transition-colors duration-200 rounded-md px-2 py-1 relative ${
                          !hasSeenBreakdownHint ? 'button-rotation-animation' : ''
                        }`}
                      >
                        {!hasSeenBreakdownHint && (
                          <>
                            <span className="anim-border"></span>
                            <span className="anim-border"></span>
                            <span className="anim-border"></span>
                            <span className="anim-border"></span>
                          </>
                        )}
                         <svg className={`w-4 h-4 transition-transform duration-200 ${showBreakdown ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                           <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                         </svg>
                         <span>{showBreakdown ? t('results.hideBreakdown') : t('results.showBreakdown')}</span>
                       </button>
                       
                       {showBreakdown && (
                         <div className="mt-4 overflow-x-auto">
                           <table className="w-full text-sm border border-green-200 rounded-lg bg-white shadow-sm">
                             <thead>
                               <tr className="bg-gradient-to-r from-green-100 to-emerald-100">
                                 <th className="px-3 py-3 text-left border-b border-green-200 font-semibold text-green-800">{t('results.step')}</th>
                                 <th className="px-3 py-3 text-right border-b border-green-200 font-semibold text-blue-700">{t('results.credits')}</th>
                                 <th className="px-3 py-3 text-right border-b border-green-200 font-semibold text-blue-800">{t('results.totalCredits')}</th>
                                 <th className="px-3 py-3 text-right border-b border-green-200 font-semibold" style={{color: "#c004ad"}}>{t('results.acoins')}</th>
                                 <th className="px-3 py-3 text-right border-b border-green-200 font-semibold" style={{color: "#a0039a"}}>{t('results.totalAcoins')}</th>
                                 <th className="px-3 py-3 text-right border-b border-green-200 font-semibold text-emerald-700">{t('results.blueprints')}</th>
                                 <th className="px-3 py-3 text-right border-b border-green-200 font-semibold text-emerald-800">{t('results.totalBlueprints')}</th>
                               </tr>
                             </thead>
                             <tbody>
                               {upgradeSteps.map((step, index) => (
                                 <tr key={index} className="border-b border-green-200 hover:bg-green-50 transition-colors duration-200">
                                   <td className="px-3 py-3 font-medium text-gray-800">{step.from} → {step.to}</td>
                                   <td className="px-3 py-3 text-right text-blue-600 font-medium">{formatNumber(step.credits)}</td>
                                   <td className="px-3 py-3 text-right font-semibold text-blue-700">{formatNumber(step.totalCredits)}</td>
                                   <td className="px-3 py-3 text-right font-medium" style={{color: "#c004ad"}}>{formatNumber(step.acoins)}</td>
                                   <td className="px-3 py-3 text-right font-semibold" style={{color: "#a0039a"}}>{formatNumber(step.totalAcoins)}</td>
                                   <td className="px-3 py-3 text-right text-emerald-600 font-medium">{formatNumber(step.blueprints)}</td>
                                   <td className="px-3 py-3 text-right font-semibold text-emerald-700">{formatNumber(step.totalBlueprints)}</td>
                                 </tr>
                               ))}
                             </tbody>
                           </table>
                           
                           {/* Summary Row */}
                           {upgradeSteps.length > 0 && (
                             <div className="mt-4 p-4 bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-lg">
                               <div className="text-center">
                                 <h4 className="text-sm font-semibold text-green-800 mb-2">Total Upgrade Cost</h4>
                                 <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                                   <div className="text-center">
                                     <div className="text-xs text-blue-600 font-medium">Credits</div>
                                     <div className="text-lg font-bold text-blue-700">{formatNumber(upgradeSteps[upgradeSteps.length - 1]?.totalCredits || 0)}</div>
                                   </div>
                                   <div className="text-center">
                                     <div className="text-xs font-medium" style={{color: "#c004ad"}}>A-Coins</div>
                                     <div className="text-lg font-bold" style={{color: "#a0039a"}}>{formatNumber(upgradeSteps[upgradeSteps.length - 1]?.totalAcoins || 0)}</div>
                                   </div>
                                   <div className="text-center">
                                     <div className="text-xs text-emerald-600 font-medium">Blueprints</div>
                                     <div className="text-lg font-bold text-emerald-700">{formatNumber(upgradeSteps[upgradeSteps.length - 1]?.totalBlueprints || 0)}</div>
                                   </div>
                                 </div>
                               </div>
                             </div>
                           )}
                         </div>
                       )}
                     </div>
         </div>
       )}

       {/* Feedback Section */}
       <div className="mt-8 sm:mt-10 pt-6 sm:pt-8 border-t border-gray-200 dark:border-gray-600">
         <div className="text-center mb-6">
           <h4 className="text-sm sm:text-base font-medium text-gray-600 dark:text-gray-300 mb-3">{t('feedback.title')}</h4>
           <a 
            href="https://forms.gle/ysNkm9FaewMtz2yS6" 
             target="_blank" 
             rel="noopener noreferrer"
             onClick={() => {
               if (typeof gtag !== 'undefined') {
                 gtag('event', 'external_link_click', {
                   'link_destination': 'feedback_form',
                   'link_location': 'calculator_footer'
                 });
               }
             }}
             className="inline-flex items-center space-x-2 bg-gradient-to-r from-amber-50 to-orange-50 px-4 py-3 rounded-lg border border-amber-200 hover:from-amber-100 hover:to-orange-100 hover:border-amber-300 hover:shadow-md transition-all duration-200 cursor-pointer group"
           >
             <svg className="w-5 h-5 text-amber-600 group-hover:text-amber-700 transition-colors duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
               <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
             </svg>
             <span className="text-sm font-medium text-amber-700 group-hover:text-amber-800 transition-colors duration-200">{t('feedback.button')}</span>
             <svg className="w-4 h-4 text-amber-500 group-hover:text-amber-600 transition-colors duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
               <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
             </svg>
           </a>
         </div>

         {/* YouTube Channels Section */}
         <div className="text-center mt-6">
           <h4 className="text-sm sm:text-base font-medium text-gray-600 dark:text-gray-300 mb-4">Subscribe to our YouTube Channels</h4>
           <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
             <a 
               href="https://youtube.com/@Emperor_Mech" 
               target="_blank" 
               rel="noopener noreferrer"
               onClick={() => {
                 if (typeof gtag !== 'undefined') {
                   gtag('event', 'external_link_click', {
                     'link_destination': 'youtube_emperor_mech',
                     'link_location': 'calculator_footer'
                   });
                 }
               }}
     
               className="inline-flex items-center space-x-2 bg-gradient-to-r from-red-50 to-red-100 dark:from-red-900/20 dark:to-red-800/20 px-4 py-3 rounded-lg border border-red-200 dark:border-red-700 hover:from-red-100 hover:to-red-200 dark:hover:from-red-800/30 dark:hover:to-red-700/30 hover:border-red-300 dark:hover:border-red-600 hover:shadow-md transition-all duration-200 cursor-pointer group"
             >
               <svg className="w-5 h-5 text-red-600 dark:text-red-400 group-hover:text-red-700 dark:group-hover:text-red-300 transition-colors duration-200" fill="currentColor" viewBox="0 0 24 24">
                 <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
               </svg>
               <span className="text-sm font-medium text-red-700 dark:text-red-300 group-hover:text-red-800 dark:group-hover:text-red-200 transition-colors duration-200">Emperor Mech</span>
               <svg className="w-4 h-4 text-red-500 dark:text-red-500 group-hover:text-red-600 dark:group-hover:text-red-400 transition-colors duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                 <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
               </svg>
             </a>
             <a 
               href="https://youtube.com/@Bhargav-q7q" 
               target="_blank" 
               rel="noopener noreferrer"
               onClick={() => {
                 if (typeof gtag !== 'undefined') {
                   gtag('event', 'external_link_click', {
                     'link_destination': 'youtube_bhargav',
                     'link_location': 'calculator_footer'
                   });
                 }
               }}
     
               className="inline-flex items-center space-x-2 bg-gradient-to-r from-red-50 to-red-100 dark:from-red-900/20 dark:to-red-800/20 px-4 py-3 rounded-lg border border-red-200 dark:border-red-700 hover:from-red-100 hover:to-red-200 dark:hover:from-red-800/30 dark:hover:to-red-700/30 hover:border-red-300 dark:hover:border-red-600 hover:shadow-md transition-all duration-200 cursor-pointer group"
             >
               <svg className="w-5 h-5 text-red-600 dark:text-red-400 group-hover:text-red-700 dark:group-hover:text-red-300 transition-colors duration-200" fill="currentColor" viewBox="0 0 24 24">
                 <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
               </svg>
               <span className="text-sm font-medium text-red-700 dark:text-red-300 group-hover:text-red-800 dark:group-hover:text-red-200 transition-colors duration-200">Bhargav</span>
               <svg className="w-4 h-4 text-red-500 dark:text-red-500 group-hover:text-red-600 dark:group-hover:text-red-400 transition-colors duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                 <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
               </svg>
             </a>
           </div>
         </div>
       </div>

       {/* Credits Section */}
       <div className="mt-8 sm:mt-10 pt-6 sm:pt-8 border-t border-gray-200 dark:border-gray-600">
         <div className="text-center">
           <h4 className="text-sm sm:text-base font-medium text-gray-600 dark:text-gray-300 mb-3">Developed with ❤️ by</h4>
                       <div className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-8">
              {/* Developer */}
              <a 
              href="https://discord.com/users/792711073420804106" 
                target="_blank" 
                rel="noopener noreferrer"
                className="flex items-center space-x-2 bg-gradient-to-r from-indigo-50 to-blue-50 px-4 py-2 rounded-lg border border-indigo-200 hover:from-indigo-100 hover:to-blue-100 hover:border-indigo-300 hover:shadow-md transition-all duration-200 cursor-pointer group"
              >
                <svg className="w-5 h-5 text-indigo-600 group-hover:text-indigo-700 transition-colors duration-200" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515a.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0a12.64 12.64 0 0 0-.617-1.25a.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057a19.9 19.9 0 0 0 5.993 3.03a.078.078 0 0 0 .084-.028a14.09 14.09 0 0 0 1.226-1.994a.076.076 0 0 0-.041-.106a13.107 13.107 0 0 1-1.872-.892a.077.077 0 0 1-.008-.128a10.2 10.2 0 0 0 .372-.292a.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127a12.299 12.299 0 0 1-1.873.892a.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028a19.839 19.839 0 0 0 6.002-3.03a.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419c0-1.333.956-2.419 2.157-2.419c1.21 0 2.176 1.096 2.157 2.42c0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419c0-1.333.955-2.419 2.157-2.419c1.21 0 2.176 1.096 2.157 2.42c0 1.333-.946 2.418-2.157 2.418z"/>
                </svg>
                <span className="text-sm font-medium text-indigo-700 group-hover:text-indigo-800 transition-colors duration-200">Developer</span>
                <span className="text-xs text-indigo-600 group-hover:text-indigo-700 transition-colors duration-200 font-mono">goku999999</span>
                <svg className="w-4 h-4 text-indigo-500 group-hover:text-indigo-600 transition-colors duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                </svg>
              </a>
              
              {/* Contributor */}
              <a 
              href="https://discord.com/users/1137839450685186079" 
                target="_blank" 
                rel="noopener noreferrer"
                className="flex items-center space-x-2 bg-gradient-to-r from-emerald-50 to-green-50 px-4 py-2 rounded-lg border border-emerald-200 hover:from-emerald-100 hover:to-green-100 hover:border-emerald-300 hover:shadow-md transition-all duration-200 cursor-pointer group"
              >
                <svg className="w-5 h-5 text-emerald-600 group-hover:text-emerald-700 transition-colors duration-200" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515a.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0a12.64 12.64 0 0 0-.617-1.25a.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057a19.9 19.9 0 0 0 5.993 3.03a.078.078 0 0 0 .084-.028a14.09 14.09 0 0 0 1.226-1.994a.076.076 0 0 0-.041-.106a13.107 13.107 0 0 1-1.872-.892a.077.077 0 0 1-.008-.128a10.2 10.2 0 0 0 .372-.292a.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127a12.299 12.299 0 0 1-1.873.892a.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028a19.839 19.839 0 0 0 6.002-3.03a.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419c0-1.333.956-2.419 2.157-2.419c1.21 0 2.176 1.096 2.157 2.42c0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419c0-1.333.955-2.419 2.157-2.419c1.21 0 2.176 1.096 2.157 2.42c0 1.333-.946 2.418-2.157 2.418z"/>
                </svg>
                <span className="text-sm font-medium text-emerald-700 group-hover:text-emerald-800 transition-colors duration-200">Contributor</span>
                <span className="text-xs text-emerald-600 group-hover:text-emerald-700 transition-colors duration-200 font-mono">prohunter0120</span>
                <svg className="w-4 h-4 text-emerald-500 group-hover:text-emerald-600 transition-colors duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                </svg>
              </a>
            {/* Contributor 2 */}
            <a
              href="https://discord.com/users/1191421412343291996"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center space-x-2 bg-gradient-to-r from-emerald-50 to-green-50 px-4 py-2 rounded-lg border border-emerald-200 hover:from-emerald-100 hover:to-green-100 hover:border-emerald-300 hover:shadow-md transition-all duration-200 cursor-pointer group"
            >
              <svg className="w-5 h-5 text-emerald-600 group-hover:text-emerald-700 transition-colors duration-200" viewBox="0 0 24 24" fill="currentColor">
                <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515a.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0a12.64 12.64 0 0 0-.617-1.25a.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057a19.9 19.9 0 0 0 5.993 3.03a.078.078 0 0 0 .084-.028a14.09 14.09 0 0 0 1.226-1.994a.076.076 0 0 0-.041-.106a13.107 13.107 0 0 1-1.872-.892a.077.077 0 0 1-.008-.128a10.2 10.2 0 0 0 .372-.292a.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127a12.299 12.299 0 0 1-1.873.892a.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028a19.839 19.839 0 0 0 6.002-3.03a.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419c0-1.333.956-2.419 2.157-2.419c1.21 0 2.176 1.096 2.157 2.42c0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419c0-1.333.955-2.419 2.157-2.419c1.21 0 2.176 1.096 2.157 2.42c0 1.333-.946 2.418-2.157 2.418z" />
              </svg>
              <span className="text-sm font-medium text-emerald-700 group-hover:text-emerald-800 transition-colors duration-200">Contributor</span>
              <span className="text-xs text-emerald-600 group-hover:text-emerald-700 transition-colors duration-200 font-mono">bhargav0457_51275</span>
              <svg className="w-4 h-4 text-emerald-500 group-hover:text-emerald-600 transition-colors duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
              </svg>
            </a>
            </div>
         </div>
        <div className="mt-4">
          <div className="w-full bg-white dark:bg-gray-800 border border-amber-200 dark:border-amber-400/30 rounded-xl shadow-sm p-4 text-center">
            <div className="mb-3 text-xs text-gray-600 dark:text-gray-400">{t('footer.support')}</div>
            <a
              href="https://www.paypal.com/paypalme/elearningexpert"
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => {
                if (typeof gtag !== 'undefined') {
                  gtag('event', 'external_link_click', {
                    'link_destination': 'support_project',
                    'link_location': 'calculator_results'
                  });
                }
              }}
              className="inline-flex items-center gap-2 rounded-full px-4 py-2
                         bg-amber-50 text-amber-800 border border-amber-200
                         hover:bg-amber-100 hover:shadow-md
                         dark:bg-amber-400/10 dark:text-amber-300 dark:border-amber-400/30 dark:hover:bg-amber-400/20
                         transition-all text-sm"
              aria-label="Support Project"
            >
              <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                <path d="M3 6.75A.75.75 0 0 1 3.75 6h12.5a.75.75 0 0 1 .75.75v1.25h1.25a2.75 2.75 0 0 1 0 5.5H17v.75a3.75 3.75 0 0 1-3.75 3.75h-2.5A3.75 3.75 0 0 1 7 14.25V6.75ZM18 8.75v4h.75a2 2 0 1 0 0-4H18Z" />
              </svg>
              {t('footer.supportProject') || 'Support Project'}
            </a>
          </div>
        </div>
       </div>
     </div>
   );
 };

export default UpgradeCalculator;
